import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useAuth } from '../contexts/AuthContext';
import { SignupData } from '../types';
import { locationService, Location } from '../services/location';
import { CheckCircleIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';
import logo from '../assets/theProposal-logo.png';

interface SignupFormData extends SignupData {
  confirmPassword: string;
}

const Signup: React.FC = () => {
  const { signup } = useAuth();
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [locations, setLocations] = useState<Location[]>([]);

  const { register, handleSubmit, watch, formState: { errors } } = useForm<SignupFormData>();

  const password = watch('password');

  useEffect(() => {
    locationService
      .getLocations()
      .then(setLocations)
      .catch((err) => console.error('Failed to load stores:', err));
  }, []);

  const onSubmit = async (data: SignupFormData) => {
    setError('');
    setLoading(true);
    
    try {
      const { confirmPassword, ...signupData } = data;
      await signup(signupData);
      setSuccess(true);
    } catch (err: any) {
      console.error('Signup error:', err.response?.data || err);
      
      if (err.response?.data?.code === 'BIZ_003' || err.response?.data?.code === 'VAL_009') {
        setError('An account with this email already exists. Please log in or use a different email.');
      } else if (err.response?.data?.code === 'VAL_003') {
        // Password validation error from backend
        setError(err.response?.data?.error || 'Password does not meet requirements. Please check and try again.');
      } else if (err.response?.data?.code === 'RATE_LIMIT') {
        setError('Too many signup attempts. Please try again in a few minutes.');
      } else if (err.response?.data?.error) {
        // Show the actual error message from the backend
        setError(err.response.data.error);
      } else {
        setError('An error occurred during signup. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen flex flex-col justify-center px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="bg-white py-8 px-4 shadow-sm rounded-lg sm:px-10">
            <div className="text-center">
              <CheckCircleIcon className="mx-auto h-12 w-12 text-green-500" />
              <h2 className="mt-4 text-2xl font-semibold text-gray-900">
                Check Your Email
              </h2>
              <p className="mt-2 text-sm text-gray-600">
                We've sent a verification link to your email address. 
                Please check your inbox and click the link to verify your account.
              </p>
              <p className="mt-4 text-sm text-gray-500">
                Didn't receive the email? Check your spam folder or{' '}
                <Link to="/resend-verification" className="text-primary-600 hover:text-primary-500">
                  request a new verification link
                </Link>
              </p>
              <Link
                to="/login"
                className="btn-primary w-full mt-8 inline-block"
              >
                Go to Login
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col justify-center px-4 sm:px-6 lg:px-8 bg-gray-50 py-12">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <img 
          src={logo} 
          alt="The Proposal Bridal" 
          className="h-16 mx-auto mb-4"
        />
        <h2 className="text-center text-xl text-gray-600">
          Create Your Account
        </h2>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow-sm rounded-lg sm:px-10">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-md p-4">
                <div className="flex">
                  <ExclamationCircleIcon className="h-5 w-5 text-red-400" />
                  <p className="ml-3 text-sm text-red-800">{error}</p>
                </div>
              </div>
            )}

            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                Full Name
              </label>
              <input
                {...register('name', { required: 'Name is required' })}
                type="text"
                autoComplete="name"
                className="input-field mt-1"
                placeholder="Jane Doe"
              />
              {errors.name && (
                <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
              )}
            </div>

            <div>
              <label htmlFor="locationId" className="block text-sm font-medium text-gray-700">
                Which store are you most likely to visit first?
              </label>
              <select
                {...register('locationId', { required: 'Please select a store' })}
                className="input-field mt-1"
                defaultValue=""
              >
                <option value="" disabled>
                  Select a store
                </option>
                {locations.map((location) => (
                  <option key={location.id} value={location.id}>
                    {location.name}
                  </option>
                ))}
              </select>
              {errors.locationId && (
                <p className="mt-1 text-sm text-red-600">{errors.locationId.message}</p>
              )}
              <p className="mt-1 text-xs text-gray-500">
                Your catalogue will show the collection available at this store.
              </p>
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Email Address
              </label>
              <input
                {...register('email', { 
                  required: 'Email is required',
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: 'Invalid email address'
                  }
                })}
                type="email"
                autoComplete="email"
                className="input-field mt-1"
                placeholder="you@example.com"
              />
              {errors.email && (
                <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
              )}
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                Password
              </label>
              <input
                {...register('password', { 
                  required: 'Password is required',
                  minLength: {
                    value: 8,
                    message: 'Password must be at least 8 characters'
                  },
                  pattern: {
                    value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*(),.?":{}|<>]).{8,}$/,
                    message: 'Password must contain uppercase, lowercase, numbers, and a special character'
                  }
                })}
                type="password"
                autoComplete="new-password"
                className="input-field mt-1"
                placeholder="••••••••"
              />
              {errors.password && (
                <p className="mt-1 text-sm text-red-600">{errors.password.message}</p>
              )}
              <p className="mt-1 text-xs text-gray-500">
                At least 8 characters with uppercase, lowercase, numbers, and a special character (!@#$%^&* etc.)
              </p>
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                Confirm Password
              </label>
              <input
                {...register('confirmPassword', { 
                  required: 'Please confirm your password',
                  validate: value => value === password || 'Passwords do not match'
                })}
                type="password"
                autoComplete="new-password"
                className="input-field mt-1"
                placeholder="••••••••"
              />
              {errors.confirmPassword && (
                <p className="mt-1 text-sm text-red-600">{errors.confirmPassword.message}</p>
              )}
            </div>

            <div className="space-y-4">
              <label className="flex items-start">
                <input
                  {...register('acceptedTerms', { 
                    required: 'You must accept the terms and conditions' 
                  })}
                  type="checkbox"
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded mt-1"
                />
                <span className="ml-2 text-sm text-gray-600">
                  I accept the{' '}
                  <Link to="/terms" className="text-primary-600 hover:text-primary-500">
                    Terms and Conditions
                  </Link>{' '}
                  and{' '}
                  <Link to="/privacy" className="text-primary-600 hover:text-primary-500">
                    Privacy Policy
                  </Link>
                </span>
              </label>
              {errors.acceptedTerms && (
                <p className="text-sm text-red-600">{errors.acceptedTerms.message}</p>
              )}
            </div>

            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full"
            >
              {loading ? 'Creating Account...' : 'Create Account'}
            </button>
          </form>

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500">Already have an account?</span>
              </div>
            </div>

            <div className="mt-6">
              <Link
                to="/login"
                className="btn-secondary w-full text-center block"
              >
                Sign In
              </Link>
            </div>
          </div>
        </div>
        
        {/* Back to Journey link */}
        <div className="mt-6 text-center">
          <a
            href="https://journey.theproposal.com.sg"
            className="text-sm text-gray-600 hover:text-gray-900 hover:underline"
          >
            Back to Journey by The Proposal Bridal
          </a>
        </div>
      </div>
    </div>
  );
};

export default Signup;